/**
 * Centralised application configuration.
 *
 * API base URL is always a relative path (e.g. /api/v1) so the
 * Vite dev proxy and production reverse proxy handle routing.
 * Never set this to an absolute URL — that would bypass the proxy
 * and cause CORS issues in development.
 */

// ── URL parameter overrides ─────────────────────────────────
//
// These let you control runtime behaviour via the address bar without
// touching .env files. Useful for demos, QA, and quick manual testing.
//
//   ?mocks=true          Force mock mode even if VITE_USE_MOCKS=false
//   ?dqx=pass|fail       Force DQX validation outcome
//

function getUrlParam(key: string): string | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get(key);
}

/** Whether mock mode is active -- `?mocks=true` URL override OR env var */
export function isUsingMocks(): boolean {
  if (getUrlParam("mocks") === "true") return true;
  return import.meta.env.VITE_USE_MOCKS === "true";
}

/** DQX override: "pass" | "fail" | null (no override) */
export function getDqxOverride(): "pass" | "fail" | null {
  const v = getUrlParam("dqx");
  if (v === "pass" || v === "fail") return v;
  return null;
}

// ── App config ──────────────────────────────────────────────
//
// IMPORTANT: `flags` uses getters so the values are evaluated at
// runtime on every access. This prevents the production minifier
// from replacing them with compile-time constants and tree-shaking
// mock code paths.

/** Derive the v2 API base from the v1 base (same host, `/api/v2` path). */
export function resolveApiV2BaseUrl(
  v1BaseUrl = import.meta.env.VITE_API_BASE_URL || "/api/v1"
): string {
  if (v1BaseUrl.endsWith("/api/v1")) {
    return `${v1BaseUrl.slice(0, -"/api/v1".length)}/api/v2`;
  }
  if (v1BaseUrl.includes("/api/v1")) {
    return v1BaseUrl.replace("/api/v1", "/api/v2");
  }
  return "/api/v2";
}

const _staticConfig = {
  api: {
    baseUrl: import.meta.env.VITE_API_BASE_URL || "/api/v1",
    get v2BaseUrl(): string {
      return resolveApiV2BaseUrl(this.baseUrl);
    },
    timeout: 360_000,
    retries: 2,
  },
  dqx: {
    databricksJobId: import.meta.env.VITE_DATABRICKS_JOB_ID || "",
    /** Relative path under the server contract base (e.g. `contracts/klyra_claims_data_contract.yaml`). Used for “Download data contract rules”. */
    dataContractRelativePath:
      (import.meta.env.VITE_DQX_DATA_CONTRACT_PATH as string | undefined)?.trim() ?? "",
  },
  upload: {
    maxFileSizeMB: 50,
    acceptedTypes: [".xlsx", ".csv", ".xml", ".json"] as const,
    acceptedMimeTypes: [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv",
      "application/csv",
      "application/vnd.ms-excel",
      "application/xml",
      "text/xml",
      "application/json",
    ] as const,
  },
  table: {
    virtualRowHeight: 35,
    overscan: 10,
    previewRowLimit: 100,
  },
} as const;

export const appConfig = {
  ..._staticConfig,

  /** Runtime toggles -- getters evaluate at runtime, surviving tree-shaking */
  flags: {
    get useMocks(): boolean {
      return isUsingMocks();
    },
    get devAuthBypass(): boolean {
      return import.meta.env.VITE_DEV_AUTH_BYPASS === "true";
    },
  },
};

export type AppConfig = typeof appConfig;
